import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BodyMapCanvas from '../components/onboarding/BodyMapCanvas';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';
import PatternRevelation from '../components/onboarding/PatternRevelation';
import IntentSelectionScreen from '../components/onboarding/IntentSelectionScreen';
import { supabase } from '../services/backendService';
import logger from "@/utils/logger";

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface BodyMapData {
    [zoneId: string]: {
        zone: string;
        sensation: string;
        color: string;
        intensity?: number;
    };
}

interface QuestionnaireData {
    patternData: Record<string, any>;
    passportData: Record<string, any>;
    vikritiSkipped: boolean;
    vikriti_decision?: 'skip' | 'continue';
}

interface DetectedPattern {
    vikritiDosha: 'vata' | 'pitta' | 'kapha' | 'vata-pitta' | 'pitta-kapha' | 'vata-kapha' | 'sama';
    primaryDosha?: string; // Alias
    vikriti_dosha?: string; // Alias
    prakritiDosha?: string;
    primaryPrakriti?: string; // Alias
    prakriti_dosha?: string; // Alias
    confidence: number;
    prakritiConfidence?: number;
    insights: string[];
    scores?: {
        prakriti: Record<string, number>;
        vikriti: Record<string, number>;
    };
    recommendations?: string[];
}

interface OnboardingData {
    userIntent: 'wellness' | 'condition' | null;
    primaryConcern: string | null;
    primaryGoal: string | null;
    bodyMap: BodyMapData | null;
    questionnaire: QuestionnaireData | null;
    pattern: DetectedPattern | null;
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN ONBOARDING PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════

// Summary Card Component for Pre-filled Segments
// Formatting helper
const formatValue = (key: string, val: any) => {
    if (val === null || val === undefined) return 'Not provided';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (key === 'sex' && typeof val === 'string') return val.charAt(0).toUpperCase() + val.slice(1);
    if (typeof val === 'object' && val !== null) {
        return Object.values(val).filter(v => v !== null && v !== undefined).join(', ');
    }
    return val;
};

const SegmentSummary = ({ title, data, onEdit }: { title: string, data: any, onEdit?: () => void }) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    const displayKeys: Record<string, string> = {
        age: 'Age',
        sex: 'Sex',
        age_sex: 'Demographics',
        height: 'Height',
        weight: 'Weight',
        height_weight: 'Metrics',
        occupation: 'Occupation',
        dietary_preference: 'Diet',
        allergies: 'Allergies',
        current_medications: 'Medications',
        medical_history: 'History',
        primary_goal: 'Primary Goal',
        primary_concern: 'Health Concern',
        health_data: 'Health Details',
        life_stage: 'Life Stage',
        menstrual_pattern: 'Menstrual Pattern',
        hormonal_context: 'Hormonal Context',
        flow_color: 'Flow Color'
    };

    // Flatten any nested keys into the top level for display
    const flattenedData: Record<string, any> = {};
    Object.entries(data).forEach(([key, val]) => {
        if (displayKeys[key]) {
            flattenedData[key] = val;
        } else if (typeof val === 'object' && val !== null) {
            // Check if sub-keys are in displayKeys
            Object.entries(val).forEach(([subKey, subVal]) => {
                if (displayKeys[subKey]) flattenedData[subKey] = subVal;
                else {
                     // Fallback for objects that aren't mapped
                     if (!flattenedData[key]) flattenedData[key] = val;
                }
            });
        }
    });

    return (
        <div className="bg-sage-50/50 rounded-2xl p-6 border border-sage-100 mb-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-black uppercase tracking-widest text-sage-600">{title}</h3>
                {onEdit && (
                    <button 
                        onClick={onEdit}
                        className="text-sage-500 hover:text-sage-700 text-xs font-bold underline"
                    >
                        Edit
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {Object.entries(flattenedData).map(([key, val]) => {
                    if (!displayKeys[key] || !val) return null;
                    return (
                        <div key={key} className="flex flex-col">
                            <span className="text-[10px] text-sage-400 uppercase font-bold">{displayKeys[key]}</span>
                            <span className="text-sm text-sage-800 font-medium truncate">{formatValue(key, val)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Review & Submit Screen Component
const ReviewAndSubmitScreen = ({ 
    onboardingData, 
    initialAnswers, 
    onEdit, 
    onSubmit, 
    isSubmitting 
}: { 
    onboardingData: OnboardingData, 
    initialAnswers: any, 
    onEdit: (step: number) => void, 
    onSubmit: () => void,
    isSubmitting: boolean
}) => {
    const [mhmdaConsent, setMhmdaConsent] = useState(false);
    const passportData = { ...initialAnswers, ...onboardingData.questionnaire?.passportData };
    const patternData = onboardingData.questionnaire?.patternData || {};
    const vikritiSkipped = onboardingData.questionnaire?.vikritiSkipped;

    return (
        <div className="w-full max-w-3xl mx-auto py-8 px-4">
            <div className="text-center mb-10">
                <h2 className="text-3xl font-serif font-bold text-sage-900 mb-2">Review Your Wellness Story</h2>
                <p className="text-sage-600">Please verify your details before we calculate your Ayurvedic pattern.</p>
            </div>

            <div className="space-y-6 mb-12">
                {/* S0 Card */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-sage-100">
                    <SegmentSummary 
                        title="Essential Details" 
                        data={passportData} 
                        onEdit={() => onEdit(3)} 
                    />
                </div>

                {/* S1 Card */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-sage-100">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-black uppercase tracking-widest text-sage-600">Current State (Vikriti)</h3>
                        <button onClick={() => onEdit(6)} className="text-sage-500 hover:text-sage-700 text-xs font-bold underline">Edit</button>
                    </div>
                    {vikritiSkipped ? (
                        <p className="text-sm text-sage-500 italic bg-sage-50/50 p-4 rounded-xl">You chose to skip the current state assessment for now.</p>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-sage-50/50 p-4 rounded-xl">
                                <p className="text-[10px] font-black uppercase tracking-widest text-sage-400 mb-1">Primary Concern</p>
                                <p className="text-sm font-medium text-sage-800">{onboardingData.primaryConcern || "Wellness Optimization"}</p>
                            </div>
                            <div className="bg-sage-50/50 p-4 rounded-xl">
                                <p className="text-[10px] font-black uppercase tracking-widest text-sage-400 mb-1">Area of Focus</p>
                                <p className="text-sm font-medium text-sage-800">
                                    {Object.values(onboardingData.bodyMap || {}).map(z => z.zone).join(', ') || "Full Body"}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* S2 Card */}
                <div className="bg-white rounded-3xl p-6 shadow-sm border border-sage-100">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-black uppercase tracking-widest text-sage-600">Natural Profile (Prakriti)</h3>
                        <button onClick={() => onEdit(8)} className="text-sage-500 hover:text-sage-700 text-xs font-bold underline">Edit</button>
                    </div>
                    {Object.keys(patternData).length === 0 ? (
                        <p className="text-sm text-sage-500 italic bg-sage-50/50 p-4 rounded-xl">Constitutional assessment pending.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                             {Object.entries(patternData).slice(0, 8).map(([key, val]: [string, any]) => (
                                <span key={key} className="px-3 py-1.5 bg-sage-50 text-sage-700 text-[10px] font-bold uppercase tracking-tight rounded-lg border border-sage-100">
                                    {key.replace(/_/g, ' ')}: {Array.isArray(val) ? val[0] : (typeof val === 'string' ? val : '✓')}
                                </span>
                             ))}
                             {Object.keys(patternData).length > 8 && <span className="text-[10px] text-sage-400 font-bold px-2 py-1">+{Object.keys(patternData).length - 8} more details</span>}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col items-center gap-6">
                <div className="flex items-start gap-4 max-w-md bg-sage-50/50 p-5 rounded-2xl border border-sage-100 shadow-sm">
                    <input 
                        type="checkbox" 
                        id="mhmda_consent" 
                        checked={mhmdaConsent} 
                        onChange={(e) => setMhmdaConsent(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-sage-300 text-sage-600 focus:ring-sage-500 cursor-pointer"
                    />
                    <label htmlFor="mhmda_consent" className="text-xs text-sage-600 leading-relaxed cursor-pointer select-none">
                        I consent to the collection and processing of my consumer health data as described in the 
                        <a href="/privacy" target="_blank" className="text-sage-800 font-bold underline ml-1 hover:text-sage-900">MHMDA Disclosure (Privacy Policy)</a>. 
                        This consent is required to generate your personalized Ayurvedic pattern.
                    </label>
                </div>

                <button 
                    onClick={onSubmit}
                    disabled={isSubmitting || !mhmdaConsent}
                    className={`w-full max-w-md py-5 px-10 rounded-full text-xl font-bold font-serif shadow-xl transition-all transform active:scale-95
                        ${(isSubmitting || !mhmdaConsent) ? 'bg-gray-300 cursor-not-allowed text-gray-500' : 'bg-sage-600 text-white hover:bg-sage-700 hover:scale-[1.02]'}`}
                >
                    {isSubmitting ? 'Finalizing...' : 'Confirm & Reveal Your Results →'}
                </button>
                <p className="text-[10px] text-sage-400 font-bold uppercase tracking-widest">Secure • Encrypted • Private</p>
            </div>
        </div>
    );
};

export default function Onboarding() {
    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(0);
    const isRefreshMode = location.state?.mode === 'vikriti-refresh';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasCompletedJourney, setHasCompletedJourney] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [prakritiComplete, setPrakritiComplete] = useState(false);
    const [vikritiComplete, setVikritiComplete] = useState(false);
    const [passportComplete, setPassportComplete] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSkipToIncomplete, setIsSkipToIncomplete] = useState(false);
    const [existingPrakriti, setExistingPrakriti] = useState<string | null>(null);
    const [onboardingData, setOnboardingData] = useState<OnboardingData>({
        userIntent: null,
        primaryConcern: null,
        primaryGoal: null,
        bodyMap: null,
        questionnaire: null,
        pattern: null,
    });

    // ═══════════════════════════════════════════════════════════════════════
    // STEP 0: Welcome Screen Logic
    // ═══════════════════════════════════════════════════════════════════════
    const handleStartJourney = async () => {
        logger.log('[Onboarding] handleStartJourney called');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            logger.log('[Onboarding] User found:', user.id);
            setUserId(user.id);
            const { data: profile } = await supabase
                .from('user_profiles')
                .select('user_intent, primary_concern')
                .eq('user_id', user.id)
                .maybeSingle();

            if (profile?.user_intent) {
                logger.log('[Onboarding] Existing intent found, skipping to step 2');
                setOnboardingData(prev => ({ 
                    ...prev, 
                    userIntent: profile.user_intent,
                    primaryConcern: profile.primary_concern 
                }));
                setStep(2);
                return;
            }
            setStep(1);
        } else {
            logger.error('[Onboarding] No user found in handleStartJourney');
            setError("Please sign in to save your progress.");
        }
    };

    const handleSkip = () => {
        navigate('/app', { state: { skippedOnboarding: true } });
    };

    // ═══════════════════════════════════════════════════════════════════════
    // STEP 1: Intent Selection Complete
    // ═══════════════════════════════════════════════════════════════════════
    const handleIntentComplete = (intent: 'wellness' | 'condition', concern: string) => {
        setOnboardingData(prev => ({ 
            ...prev, 
            userIntent: intent, 
            primaryConcern: concern,
            questionnaire: {
                patternData: {},
                passportData: {},
                vikritiSkipped: false
            }
        }));
        setStep(2); // Go to Questionnaire (Prakriti track first)
    };

    // ═══════════════════════════════════════════════════════════════════════
    // MODE: Complete Profile (Partial Onboarding)
    // ═══════════════════════════════════════════════════════════════════════
    const [initialAnswers, setInitialAnswers] = useState<Record<string, any>>({});
    const [isCompleteMode, setIsCompleteMode] = useState(false);

    React.useEffect(() => {
        const checkCompletionStatus = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                const { data: journey } = await supabase.from('user_journey').select('onboarding_completed, prakriti_complete, vikriti_complete').eq('user_id', user.id).maybeSingle();
                if (journey?.onboarding_completed) {
                    setHasCompletedJourney(true);
                }
                if (journey?.prakriti_complete) setPrakritiComplete(true);
                if (journey?.vikriti_complete) setVikritiComplete(true);
                
                // Track if we should only show incomplete segments
                if ((location as any).state?.skipToIncomplete) {
                    setIsSkipToIncomplete(true);
                }
                
                setUserId(user.id);

                // Check for Vikriti Refresh or Prakriti Retake Mode from navigation state
                const navMode = (location as any).state?.mode;
                if (navMode === 'vikriti-refresh' || navMode === 'prakriti-retake' || navMode === 'essential-details') {
                    setLoading(true);
                    const loadModeData = async () => {
                        try {
                            const { data: profile } = await supabase.from('user_profiles').select('*').eq('user_id', user.id).maybeSingle();
                            const { data: onboarding } = await supabase.from('onboarding_profiles').select('*').eq('user_id', user.id).maybeSingle();
                            
                            if (profile) {
                                setExistingPrakriti(profile.prakriti_dosha);
                                const health = profile.health_data || {};

                                if (navMode === 'prakriti-retake') {
                                    if ((location as any).state?.skipToIncomplete) {
                                        const mockQuestionnaire: QuestionnaireData = {
                                            patternData: onboarding?.lifestyle_profile || {},
                                            passportData: {
                                                age_sex: { age: profile.age, sex: profile.sex?.toLowerCase() },
                                                height_weight: { height: health.height, weight: health.weight },
                                                dietary_preference: health.dietary_preference?.toLowerCase(),
                                                allergies: profile.allergies,
                                                current_medications: profile.medications,
                                                medical_history: health.medical_history,
                                                primary_goal: profile.primary_goal,
                                                occupation: profile.occupation
                                            },
                                            vikritiSkipped: onboarding?.vikriti_skipped || false
                                        };
                                        setOnboardingData(prev => ({ 
                                            ...prev, 
                                            userIntent: profile.user_intent,
                                            primaryConcern: profile.primary_concern,
                                            questionnaire: mockQuestionnaire 
                                        }));

                                        const s0Done = !!(profile.age && profile.sex && health.height && health.weight);
                                        const s1Done = !!(profile.vikriti_dosha || onboarding?.vikriti_skipped);
                                        const s2Done = !!profile.prakriti_dosha;

                                        setPassportComplete(s0Done);
                                        setVikritiComplete(s1Done);
                                        setPrakritiComplete(s2Done);

                                        if (!s0Done) handleSegmentNavigate('passport');
                                        else if (!s1Done) handleSegmentNavigate('vikriti-transition');
                                        else if (!s2Done) handleSegmentNavigate('prakriti-transition');
                                        else setStep(10);
                                    } else {
                                        await supabase.from('onboarding_profiles').update({
                                            prakriti_dosha: null,
                                            vikriti_dosha: null,
                                            prakriti_scores: null,
                                            vikriti_scores: null,
                                            prakriti_confidence: null,
                                            vikriti_confidence: null,
                                            affected_zones: null,
                                            onset_timeframe: null,
                                            meal_regularity: null,
                                            sleep_pattern: null,
                                            stress_level: null,
                                            emotional_state: [],
                                            pattern_insights: null,
                                            lifestyle_profile: {}
                                        }).eq('user_id', user.id);

                                        setOnboardingData(prev => ({ 
                                            ...prev, 
                                            userIntent: profile.user_intent,
                                            primaryConcern: profile.primary_concern,
                                            questionnaire: {
                                                patternData: {},
                                                passportData: {},
                                                vikritiSkipped: false
                                            }
                                        }));
                                        setStep(1);
                                    }
                                } else if (navMode === 'vikriti-refresh') {
                                    await supabase.from('onboarding_profiles').update({
                                        vikriti_dosha: null,
                                        vikriti_scores: null,
                                        vikriti_confidence: null,
                                        affected_zones: null,
                                        onset_timeframe: null,
                                        meal_regularity: null,
                                        sleep_pattern: null, // Corrected from sleep_quality
                                        stress_level: null,
                                        emotional_state: [],
                                    }).eq('user_id', user.id);

                                    const mockQuestionnaire: QuestionnaireData = {
                                        patternData: {
                                            ...onboarding?.lifestyle_profile,
                                            onset: null,
                                            meal_timing: null,
                                            sleep_pattern: null, // Corrected from sleep_quality
                                            stress_level: [],
                                        },
                                        passportData: {
                                            age_sex: { age: profile.age, sex: profile.sex?.toLowerCase() },
                                            height_weight: { height: health.height, weight: health.weight },
                                            dietary_preference: health.dietary_preference?.toLowerCase(),
                                            allergies: profile.allergies,
                                            current_medications: profile.medications,
                                            medical_history: health.medical_history,
                                            primary_goal: profile.primary_goal,
                                        },
                                        vikritiSkipped: false
                                    };

                                    setInitialAnswers({ ...mockQuestionnaire.patternData, ...mockQuestionnaire.passportData });
                                    setOnboardingData(prev => ({ 
                                        ...prev, 
                                        userIntent: (profile.primary_concern && profile.primary_concern !== 'wellness') ? 'condition' : 'wellness',
                                        primaryConcern: profile.primary_concern,
                                        questionnaire: mockQuestionnaire 
                                    }));
                                    setStep(5);
                                } else if (navMode === 'essential-details') {
                                    const mockQuestionnaire: QuestionnaireData = {
                                        patternData: onboarding?.lifestyle_profile || {},
                                        passportData: {
                                            age_sex: { age: profile.age, sex: profile.sex?.toLowerCase() },
                                            height_weight: { height: health.height, weight: health.weight },
                                            dietary_preference: health.dietary_preference?.toLowerCase(),
                                            allergies: profile.allergies,
                                            current_medications: profile.medications,
                                            medical_history: health.medical_history,
                                            primary_goal: profile.primary_goal,
                                        },
                                        vikritiSkipped: onboarding?.vikriti_skipped || false
                                    };
                                    setInitialAnswers({ ...mockQuestionnaire.patternData, ...mockQuestionnaire.passportData });
                                    setOnboardingData(prev => ({ 
                                        ...prev, 
                                        userIntent: (profile.primary_concern && profile.primary_concern !== 'wellness') ? 'condition' : 'wellness',
                                        primaryConcern: profile.primary_concern,
                                        questionnaire: mockQuestionnaire 
                                    }));
                                    setStep(3);
                                }
                            }
                        } catch (err) {
                            logger.error('[Onboarding] loadModeData Error:', err);
                        } finally {
                            setLoading(false);
                        }
                    };
                    loadModeData();
                    return;
                }
            }
        };

        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');

        if (mode === 'complete') {
            setIsCompleteMode(true);
            setLoading(true);
            const loadData = async () => {
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                const { data: onboarding } = await supabase.from('onboarding_profiles').select('*').eq('user_id', user.id).maybeSingle();
                if (onboarding) {
                    setOnboardingData(prev => ({ ...prev, bodyMap: onboarding.affected_zones }));
                }

                const { data: profile } = await supabase.from('user_profiles').select('*').eq('id', user.id).single();

                if (profile) {
                    const health = profile.health_data || {};
                    const answers: Record<string, any> = {
                        onset: onboarding?.onset_timeframe,
                        meal_timing: onboarding?.meal_regularity,
                        meal_feeling: health.meal_feeling,
                        sleep_quality: onboarding?.sleep_quality,
                        energy_pattern: health.energy_pattern,
                        stress_level: onboarding?.emotional_state || [],
                        life_events: health.life_events || [],
                        age_sex: { age: profile.age, sex: profile.sex?.toLowerCase() },
                        height_weight: { height: health.height, weight: health.weight },
                        dietary_preference: health.dietary_preference?.toLowerCase(),
                        allergies: profile.allergies,
                        current_medications: profile.medications,
                        medical_history: health.medical_history,
                        primary_goal: profile.primary_goal
                    };

                    Object.keys(answers).forEach(key => {
                        if (answers[key] === undefined || answers[key] === null) delete answers[key];
                    });

                    setInitialAnswers(answers);
                    setStep(2);
                }
                setLoading(false);
            };
            loadData();
        } else {
            checkCompletionStatus();
        }
    }, [location.state, navigate]);

    // Helper to find the next incomplete step or go to review
    const navigateNext = (currentStep: number) => {
        if (!isSkipToIncomplete) {
            if (currentStep === 3) return 4;
            if (currentStep === 4) return 5;
            if (currentStep === 6) return 7;
            if (currentStep === 8) return 10;
            return currentStep + 1;
        }

        // Logic for skipToIncomplete mode
        // Check Passport (S0)
        if (!passportComplete && currentStep < 3) return 3;
        
        // Check Vikriti (S1)
        if (!vikritiComplete && currentStep < 4) return 4;
        
        // Check Prakriti (S2)
        if (!prakritiComplete && currentStep < 7) return 7;
        
        // If all done, go to Review
        return 10;
    };

    // New handler for Primary Goal step
    const handleGoalComplete = (data: any) => {
        const goal = data.answers?.primary_goal;
        const concern = data.answers?.primary_concern;
        
        setOnboardingData(prev => ({
            ...prev,
            primaryGoal: goal,
            primaryConcern: concern || prev.primaryConcern,
            questionnaire: {
                ...prev.questionnaire!,
                passportData: {
                    ...prev.questionnaire?.passportData,
                    primary_goal: goal,
                    primary_concern: concern || prev.questionnaire?.passportData?.primary_concern
                }
            }
        }));
        setStep(navigateNext(2)); 
    };

    const handlePassportComplete = (data: any) => {
        logger.log('[Onboarding] handlePassportComplete:', data);
        setOnboardingData(prev => ({
            ...prev,
            questionnaire: {
                ...(prev.questionnaire || { patternData: {}, passportData: {}, vikritiSkipped: false }),
                passportData: data.passportData
            }
        }));
        setPassportComplete(true);
        setStep(navigateNext(3));
    };

    const handlePrakritiStepComplete = async (data: any) => {
        setPrakritiComplete(true);
        setOnboardingData(prev => ({
            ...prev,
            questionnaire: {
                ...prev.questionnaire!,
                patternData: { ...prev.questionnaire?.patternData, ...data.patternData },
                passportData: { ...prev.questionnaire?.passportData, ...data.passportData },
            }
        }));
        
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                await supabase.from('user_journey').update({ prakriti_complete: true }).eq('user_id', user.id);
            }
        } catch (err) {
            logger.error('Error updating journey in handlePrakritiStepComplete:', err);
        }

        setStep(navigateNext(8)); 
    };

    const handleReviewSubmit = async () => {
        setLoading(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const pattern = await analyzePattern({
                user_id: user?.id || 'anonymous',
                user_intent: onboardingData.userIntent || 'wellness',
                primary_concern: onboardingData.primaryConcern,
                bodyMap: onboardingData.bodyMap || {},
                lifestyle: onboardingData.questionnaire?.patternData || {},
                demographics: { ...initialAnswers, ...onboardingData.questionnaire?.passportData },
            });

            setOnboardingData(prev => ({ 
                ...prev, 
                pattern
            }));
            setStep(9); // Show Results
        } catch (err) {
            logger.error('Error in handleReviewSubmit:', err);
            setError('Failed to calculate results. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVikritiTransitionComplete = async (data: any) => {
        if (!data) return;
        const skipped = data.answers?.vikriti_decision === 'skip';
        setOnboardingData(prev => ({
            ...prev,
            questionnaire: {
                ...prev.questionnaire!,
                ...data.answers,
                vikritiSkipped: skipped
            }
        }));

        if (skipped) {
            setVikritiComplete(true); // Mark as complete since user made a decision
            setStep(7); // Jump directly to Prakriti Transition
        } else {
            setStep(5);
        }
    };

    const handleBodyMapComplete = async (bodyMapData: BodyMapData) => {
        setOnboardingData(prev => ({ ...prev, bodyMap: bodyMapData }));
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                await supabase.from('user_journey').update({ body_map_complete: true }).eq('user_id', user.id);
            }
        } catch (err) {
            logger.error('Error saving Body Map progress:', err);
        }
        setStep(6);
    };

    const handleSegmentNavigate = (segment: string) => {
        if (segment === 'passport' || segment === 'primary-goal') setStep(3);
        else if (segment === 'vikriti' || segment === 'vikriti-transition') setStep(4);
        else if (segment === 'prakriti' || segment === 'prakriti-transition') setStep(7);
    };

    const handleQuestionnaireComplete = async (questionnaireData: QuestionnaireData) => {
        setOnboardingData(prev => ({ 
            ...prev, 
            questionnaire: {
                ...(prev.questionnaire || {}),
                ...questionnaireData,
                patternData: { ...(prev.questionnaire?.patternData || {}), ...questionnaireData.patternData },
                passportData: { ...(prev.questionnaire?.passportData || {}), ...questionnaireData.passportData }
            }
        }));
        
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
            try {
                // Use upsert to ensure the row exists if it was dropped/recreated
                const { error } = await supabase.from('user_journey').upsert({ 
                    user_id: currentUser.id,
                    vikriti_complete: !questionnaireData.vikritiSkipped,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'user_id' });
                
                if (error) logger.error('Error upserting journey progress:', JSON.stringify(error, null, 2));
            } catch (err) {
                logger.error('Error in journey progress logic:', err);
            }
        }

        setLoading(true);
        try {
            const finalQuestionnaire = {
                ...(onboardingData.questionnaire || {}),
                ...questionnaireData,
                vikritiSkipped: isRefreshMode ? false : (questionnaireData.vikritiSkipped === true ? true : (onboardingData.questionnaire?.vikritiSkipped === true && questionnaireData.vikritiSkipped === undefined ? true : false)),
                patternData: { ...(onboardingData.questionnaire?.patternData || {}), ...questionnaireData.patternData },
                passportData: { ...(onboardingData.questionnaire?.passportData || {}), ...questionnaireData.passportData }
            };

            const pattern = await analyzePattern({
                user_id: currentUser?.id || 'anonymous',
                user_intent: onboardingData.userIntent || 'wellness',
                primary_concern: onboardingData.primaryConcern,
                bodyMap: onboardingData.bodyMap || {},
                lifestyle: finalQuestionnaire.patternData,
                demographics: finalQuestionnaire.passportData,
            });
            
            setOnboardingData(prev => ({ ...prev, pattern, questionnaire: finalQuestionnaire }));
            setStep(8);
        } catch (err: any) {
            logger.error('[Onboarding] handleQuestionnaireComplete ERROR:', err);
            setError('Failed to analyze your pattern. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOnboardingComplete = async (targetView?: string) => {
        if (loading) return; 
        setLoading(true);
        setError(null);

        const retryOperation = async <T,>(operation: () => Promise<T>, retries = 3, delay = 1000): Promise<T> => {
            try { return await operation(); } catch (err) {
                if (retries > 0) {
                    await new Promise(resolve => setTimeout(resolve, delay));
                    return retryOperation(operation, retries - 1, delay * 2);
                }
                throw err;
            }
        };

        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const { questionnaire, pattern, bodyMap, userIntent, primaryConcern } = onboardingData;
            if (!questionnaire || !pattern) throw new Error('Incomplete onboarding data');

            const passportData = questionnaire.passportData || {};

            // 1. Save to onboarding_profiles
            try {
                const { data: existingOnboarding } = await supabase.from('onboarding_profiles').select('created_at').eq('user_id', user.id).maybeSingle();
                
                // Robust property extraction (leveraging updated DetectedPattern interface)
                const vDosha = (pattern.vikritiDosha || pattern.primaryDosha || pattern.vikriti_dosha)?.toLowerCase() || null;
                const pDosha = (pattern.prakritiDosha || pattern.primaryPrakriti || pattern.prakriti_dosha)?.toLowerCase() || null;

                const onboardingUpdate: any = {
                    user_id: user.id,
                    user_intent: userIntent?.toLowerCase(),
                    primary_concern: primaryConcern,
                    affected_zones: bodyMap || {},
                    vikriti_dosha: (questionnaire.vikritiSkipped || !vDosha) ? null : vDosha, 
                    prakriti_dosha: isRefreshMode 
                        ? (existingPrakriti?.toLowerCase() || pDosha) 
                        : (pDosha || null),
                    pattern_insights: pattern.insights,
                    prakriti_scores: pattern.scores?.prakriti || null,
                    prakriti_confidence: pattern.prakritiConfidence || (pattern as any).prakriti_confidence || null,
                    vikriti_scores: questionnaire.vikritiSkipped ? null : (pattern.scores?.vikriti || null),
                    vikriti_confidence: questionnaire.vikritiSkipped ? null : (pattern.confidence || null),
                    onset_timeframe: questionnaire.patternData.onset || null,
                    meal_regularity: questionnaire.patternData.meal_timing || null,
                    sleep_pattern: questionnaire.patternData.sleep_pattern || null,
                    stress_level: questionnaire.patternData.stress_intensity || null, 
                    emotional_state: Array.isArray(questionnaire.patternData.stress_level) ? questionnaire.patternData.stress_level : (questionnaire.patternData.stress_level ? [questionnaire.patternData.stress_level] : []),
                    occupation: (passportData.occupation || (initialAnswers as any)?.occupation) || null,
                    life_stage: passportData.life_stage || null,
                    menstrual_pattern: passportData.menstrual_pattern || null,
                    hormonal_context: passportData.hormonal_context || null,
                    flow_color: passportData.flow_color || null,
                    lifestyle_profile: questionnaire.patternData,
                    completed_at: new Date().toISOString(),
                    created_at: (existingOnboarding as any)?.created_at || new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    vikriti_last_updated: questionnaire.vikritiSkipped ? null : new Date().toISOString(),
                    vikriti_skipped: questionnaire.vikritiSkipped || false
                };

                logger.log('[Onboarding] Submitting to onboarding_profiles:', { 
                    userId: user.id, 
                    vikriti: onboardingUpdate.vikriti_dosha, 
                    prakriti: onboardingUpdate.prakriti_dosha 
                });

                const { error: opError } = await supabase.from('onboarding_profiles').upsert(onboardingUpdate, { onConflict: 'user_id' });
                if (opError) {
                    logger.error('[Onboarding] onboarding_profiles save error details:', JSON.stringify(opError, null, 2));
                    throw new Error(`Onboarding sync failed: ${opError.message}`);
                }
            } catch (err: any) {
                logger.error('[Onboarding] Fatal error in onboarding_profiles save:', err);
                setError(err.message || 'Failed to sync onboarding results.');
                setLoading(false);
                return; // Stop here!
            }

            // 2. Save to user_profiles
            try {
                const vDosha = (pattern.vikritiDosha || pattern.primaryDosha || pattern.vikriti_dosha)?.toLowerCase() || null;
                const pDosha = (pattern.prakritiDosha || pattern.primaryPrakriti || pattern.prakriti_dosha)?.toLowerCase() || null;

                const profileUpdate: any = {
                    user_intent: userIntent?.toLowerCase(),
                    primary_concern: primaryConcern,
                    vikriti_dosha: (questionnaire.vikritiSkipped || !vDosha) ? null : vDosha,
                    prakriti_dosha: isRefreshMode 
                        ? (existingPrakriti?.toLowerCase() || pDosha) 
                        : (pDosha || null),
                    updated_at: new Date().toISOString()
                };

                if (passportData.age_sex?.age) profileUpdate.age = passportData.age_sex.age;
                if (passportData.age_sex?.sex) profileUpdate.sex = passportData.age_sex.sex.toLowerCase();
                if (passportData.occupation) profileUpdate.occupation = passportData.occupation;
                if (passportData.life_stage) profileUpdate.life_stage = passportData.life_stage;
                if (passportData.menstrual_pattern) profileUpdate.menstrual_pattern = passportData.menstrual_pattern;
                if (passportData.hormonal_context) profileUpdate.hormonal_context = passportData.hormonal_context;
                if (passportData.flow_color) profileUpdate.flow_color = passportData.flow_color;
                if (passportData.allergies?.length > 0) profileUpdate.allergies = passportData.allergies;
                if (passportData.primary_goal) profileUpdate.primary_goal = passportData.primary_goal;
                
                if (questionnaire.patternData.activity_level) profileUpdate.activity_level = parseInt(questionnaire.patternData.activity_level);
                if (questionnaire.patternData.sleep_pattern) profileUpdate.sleep_pattern = questionnaire.patternData.sleep_pattern;
                if (questionnaire.patternData.stress_intensity) profileUpdate.stress_level = parseInt(questionnaire.patternData.stress_intensity);
                
                if (passportData.dietary_preference) profileUpdate.dietary_preference = passportData.dietary_preference.toLowerCase();
                if (passportData.height_weight?.height) profileUpdate.height = passportData.height_weight.height;
                if (passportData.height_weight?.weight) profileUpdate.weight = passportData.height_weight.weight;

                const hData: any = {
                    prakriti_dosha: profileUpdate.prakriti_dosha,
                    vikriti_dosha: profileUpdate.vikriti_dosha,
                    onboarding_completed_at: new Date().toISOString()
                };
                if (passportData.current_medications?.length > 0) hData.medications = passportData.current_medications;
                if (passportData.medical_history?.length > 0) hData.medical_history = passportData.medical_history;
                if (passportData.dietary_preference) hData.dietary_preference = passportData.dietary_preference.toLowerCase();
                if (passportData.height_weight?.height) hData.height = passportData.height_weight.height;
                if (passportData.height_weight?.weight) hData.weight = passportData.height_weight.weight;
                profileUpdate.health_data = hData;

                logger.log('[Onboarding] Updating user_profiles:', { 
                    userId: user.id, 
                    profileUpdate 
                });

                const { error: upError } = await supabase.from('user_profiles').update(profileUpdate).eq('user_id', user.id);
                if (upError) throw upError;
            } catch (err: any) {
                logger.error('[Onboarding] user_profiles save error:', err);
                setError(`Passport sync failed: ${err.message || 'Database error'}`);
                setLoading(false);
                return; // Stop here!
            }

            // 3. Mark journey complete (Use upsert to prevent redirect bugs)
            await supabase.from('user_journey').upsert({
                user_id: user.id,
                onboarding_completed: true,
                prakriti_complete: true,
                vikriti_complete: !questionnaire.vikritiSkipped,
                first_consultation_ready: true,
                updated_at: new Date().toISOString()
            }, { onConflict: 'user_id' });

            const finalVikriti = (pattern.vikriti_dosha || pattern.primaryDosha || pattern.vikritiDosha || 'current')?.toLowerCase();

            navigate('/app', {
                state: {
                    view: targetView || 'chat',
                    firstTimeUser: true,
                    welcomeMessage: `Welcome! I understand your ${finalVikriti} profile. Let's begin your healing journey. 🌿`,
                    showOnboardingSuccess: true,
                },
            });

        } catch (err: any) {
            logger.error('Onboarding save error:', err);
            setError(err.message || 'Failed to save data. Please try again.');
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════

    const getAffectedSystem = () => {
        if (!onboardingData.bodyMap) return 'general';
        const zones = Object.keys(onboardingData.bodyMap);
        if (zones.some(z => z.includes('stomach') || z.includes('abdomen'))) return 'digestive';
        if (zones.some(z => z.includes('joint') || z.includes('knee'))) return 'musculoskeletal';
        return 'general';
    };
    const affectedSystem = getAffectedSystem();

    const formatBodyConnections = () => {
        if (!onboardingData.bodyMap) return [];
        return Object.entries(onboardingData.bodyMap).map(([zoneId, data]) => ({
            symptom: `${data.sensation} sensation`,
            location: data.zone,
            category: 'Imbalance Detection',
        }));
    };

    const formatLifestyleFactors = () => {
        if (!onboardingData.questionnaire) return [];
        const { patternData } = onboardingData.questionnaire;
        const factors = [];
        if (patternData.meal_timing === 'irregular') factors.push({ icon: '🔀', label: 'Irregular meals' });
        if (patternData.sleep_quality !== 'deep-refreshing') factors.push({ icon: '😴', label: 'Disrupted sleep' });
        return factors;
    };

    const isRetakeMode = (location as any).state?.mode === 'prakriti-retake' || (location as any).state?.mode === 'vikriti-refresh' || (location as any).state?.mode === 'essential-details';
    
    const getCompletedSegments = () => {
        const completed = [];
        const navMode = (location as any).state?.mode;
        
        // S0: Passport
        const hasPassportData = onboardingData.questionnaire?.passportData?.age_sex;
        if (passportComplete || hasPassportData) completed.push('passport');
        
        // S1: Vikriti
        const hasVikritiData = Object.keys(onboardingData.questionnaire?.patternData || {}).some(k => !['energy_pattern', 'weather_sensitivity', 'body_frame', 'natural_digestion', 'skin_hair_type', 'natural_temperament'].includes(k));
        if (hasVikritiData && !onboardingData.questionnaire?.vikritiSkipped && navMode !== 'vikriti-refresh') completed.push('vikriti');
        
        // S2: Prakriti
        if (prakritiComplete && navMode !== 'prakriti-retake') completed.push('prakriti');
        
        return completed;
    };

    const getSkippedSegments = () => {
        const skipped = [];
        if (onboardingData.questionnaire?.vikriti_decision === 'skip') skipped.push('vikriti');
        return skipped;
    };

    const completedSegments = getCompletedSegments();
    const skippedSegments = getSkippedSegments();

    return (
        <div className="min-h-screen bg-gradient-to-b from-sage-50 to-white font-sans">
            {error && (
                <div className="fixed top-4 right-4 max-w-md bg-red-50 border-2 border-red-200 rounded-2xl p-4 shadow-lg z-[60]">
                    <p className="font-bold text-red-900">Error: <span className="font-normal text-red-800">{error}</span></p>
                    <button onClick={() => setError(null)} className="absolute top-2 right-2">✕</button>
                </div>
            )}

            {loading && (
                <div className="fixed inset-0 bg-sage-900/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-3xl text-center shadow-xl">
                        <div className="animate-spin w-12 h-12 border-4 border-sage-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="font-serif font-bold text-sage-900">Analyzing your story...</p>
                    </div>
                </div>
            )}

            {step === 0 && (
                <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center">
                    <span className="text-6xl mb-6">🌿</span>
                    <h1 className="text-4xl md:text-5xl font-serif text-sage-900 mb-6">Start Your Health Story</h1>
                    <p className="text-lg text-sage-600 mb-10 max-w-md">Discover your unique Ayurvedic nature and find balance with Nature Nani.</p>
                    {userId ? (
                        <button onClick={handleStartJourney} className="bg-sage-600 text-white px-10 py-5 rounded-full text-xl font-bold shadow-lg hover:bg-sage-700 transition-all transform hover:scale-105">
                            Begin Journey
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-amber-600 font-medium bg-amber-50 px-4 py-2 rounded-xl border border-amber-100 mb-4">You need to be signed in to save your profile.</p>
                            <button 
                                onClick={() => navigate('/app', { state: { showAuth: true } })}
                                className="bg-sage-600 text-white px-10 py-5 rounded-full text-xl font-bold shadow-lg hover:bg-sage-700 transition-all transform hover:scale-105"
                            >
                                Sign In to Begin
                            </button>
                        </div>
                    )}
                    <button onClick={handleSkip} className="mt-6 text-sage-400 text-sm hover:text-sage-600">Skip for now</button>
                </div>
            )}

            {step === 1 && <IntentSelectionScreen userId={userId || ''} onNext={handleIntentComplete} />}

            {step === 2 && (
                <OnboardingFlow 
                    userIntent={onboardingData.userIntent || 'wellness'}
                    primaryConcern={onboardingData.primaryConcern}
                    initialData={{...initialAnswers, ...onboardingData.questionnaire?.passportData}}
                    segment="primary-goal"
                    completedSegments={completedSegments}
                    onSegmentNavigate={handleSegmentNavigate}
                    onComplete={handleGoalComplete} 
                    skipAnswered={isCompleteMode}
                />
            )}

            {step === 3 && (
                <div className="w-full max-w-2xl mx-auto">
                    {(isRetakeMode || hasCompletedJourney || completedSegments.includes('passport')) && !isEditMode && (
                        <div className="mb-8">
                            <SegmentSummary 
                                title="Essential Details" 
                                data={{
                                    ...initialAnswers, 
                                    ...onboardingData.questionnaire?.passportData,
                                    primary_concern: onboardingData.primaryConcern,
                                    primary_goal: onboardingData.primaryGoal || initialAnswers?.primary_goal
                                }} 
                                onEdit={() => setIsEditMode(true)} 
                            />
                            <div className="flex flex-col items-center gap-4">
                                <button 
                                    onClick={() => handlePassportComplete({ 
                                        answers: {...initialAnswers, ...onboardingData.questionnaire?.passportData}, 
                                        passportData: {...initialAnswers, ...onboardingData.questionnaire?.passportData, primary_goal: onboardingData.primaryGoal, primary_concern: onboardingData.primaryConcern} 
                                    })}
                                    className="bg-sage-600 text-white px-8 py-4 rounded-full font-bold shadow-md hover:bg-sage-700 transition-all transform hover:scale-105"
                                >
                                    Continue with your Essential Details
                                </button>
                                {(completedSegments.includes('vikriti') || completedSegments.includes('prakriti')) && (
                                    <button 
                                        onClick={() => {
                                            if (completedSegments.includes('prakriti')) setStep(8);
                                            else if (completedSegments.includes('vikriti')) setStep(6);
                                            else setStep(4);
                                        }}
                                        className="text-sage-500 hover:text-sage-700 text-sm font-bold underline transition-colors"
                                    >
                                        Return to active assessment
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {( (!isRetakeMode && !hasCompletedJourney && !completedSegments.includes('passport')) || isEditMode ) && (
                        <OnboardingFlow 
                            userIntent={onboardingData.userIntent || 'wellness'}
                            primaryConcern={onboardingData.primaryConcern}
                            initialData={{...initialAnswers, ...onboardingData.questionnaire?.passportData}}
                            segment="passport"
                            completedSegments={completedSegments}
                            onSegmentNavigate={handleSegmentNavigate}
                            onComplete={(data) => {
                                setIsEditMode(false);
                                handlePassportComplete(data);
                            }} 
                            skipAnswered={isCompleteMode}
                        />
                    )}
                </div>
            )}

            {step === 4 && (
                <OnboardingFlow 
                    userIntent={onboardingData.userIntent || 'wellness'}
                    primaryConcern={onboardingData.primaryConcern}
                    initialData={{...initialAnswers, ...onboardingData.questionnaire?.passportData, ...onboardingData.questionnaire?.patternData}}
                    segment="vikriti-transition"
                    completedSegments={completedSegments}
                    skippedSegments={skippedSegments}
                    isRefreshMode={isRefreshMode}
                    onSegmentNavigate={handleSegmentNavigate}
                    onVikritiComplete={handleVikritiTransitionComplete}
                    onComplete={() => {}} 
                    skipAnswered={isCompleteMode}
                />
            )}

            {step === 5 && (
                <BodyMapCanvas onComplete={handleBodyMapComplete} hideEducationalLink={hasCompletedJourney} />
            )}

            {step === 6 && (
                <OnboardingFlow 
                    userIntent={onboardingData.userIntent || 'wellness'}
                    primaryConcern={onboardingData.primaryConcern}
                    affectedSystem={affectedSystem}
                    initialData={{...onboardingData.questionnaire?.passportData, ...onboardingData.questionnaire?.patternData}}
                    segment="vikriti-questions"
                    completedSegments={completedSegments}
                    skippedSegments={skippedSegments}
                    isRefreshMode={isRefreshMode}
                    vikritiSkipped={onboardingData.questionnaire?.vikritiSkipped}
                    onSegmentNavigate={handleSegmentNavigate}
                    onComplete={async (data: any) => {
                        setOnboardingData(prev => ({ 
                            ...prev, 
                            questionnaire: { ...prev.questionnaire!, ...data } 
                        }));
                        
                        if (isRefreshMode) {
                            // Refresh mode: Jump straight to results after Vikriti
                            setLoading(true);
                            try {
                                const pattern = await analyzePattern({
                                    user_id: userId || 'anonymous',
                                    user_intent: onboardingData.userIntent || 'wellness',
                                    primary_concern: onboardingData.primaryConcern,
                                    bodyMap: onboardingData.bodyMap || {},
                                    lifestyle: { ...onboardingData.questionnaire?.patternData, ...data.patternData },
                                    demographics: { ...onboardingData.questionnaire?.passportData, ...data.passportData },
                                });
                                setOnboardingData(prev => ({ ...prev, pattern }));
                                setStep(9); // Results
                            } catch (err) {
                                logger.error('Error calculating results after refresh:', err);
                                setError('Failed to calculate results. Please try again.');
                            } finally {
                                setLoading(false);
                            }
                        } else {
                            setVikritiComplete(true);
                            setStep(navigateNext(6)); 
                        }
                    }} 
                    skipAnswered={isCompleteMode}
                />
            )}

            {step === 7 && (
                <OnboardingFlow 
                    userIntent={onboardingData.userIntent || 'wellness'}
                    primaryConcern={onboardingData.primaryConcern}
                    initialData={{...onboardingData.questionnaire?.passportData, ...onboardingData.questionnaire?.patternData}}
                    segment="prakriti-transition"
                    completedSegments={completedSegments}
                    skippedSegments={skippedSegments}
                    isRefreshMode={isRefreshMode}
                    vikritiSkipped={onboardingData.questionnaire?.vikritiSkipped}
                    onSegmentNavigate={handleSegmentNavigate}
                    onComplete={async (data: any) => {
                        if (data.prakritiSkipped) {
                            setLoading(true);
                            try {
                                const pattern = await analyzePattern({
                                    user_id: userId || 'anonymous',
                                    user_intent: onboardingData.userIntent || 'wellness',
                                    primary_concern: onboardingData.primaryConcern,
                                    bodyMap: onboardingData.bodyMap || {},
                                    lifestyle: { ...onboardingData.questionnaire?.patternData, ...data.patternData },
                                    demographics: { ...onboardingData.questionnaire?.passportData, ...data.passportData },
                                });
                                setOnboardingData(prev => ({ ...prev, pattern }));
                                setStep(9); // Results
                            } finally {
                                setLoading(false);
                            }
                        } else {
                            setStep(8); // Prakriti Questions
                        }
                    }}
                    skipAnswered={isCompleteMode}
                />
            )}

            {step === 8 && (
                <OnboardingFlow 
                    userIntent={onboardingData.userIntent || 'wellness'}
                    primaryConcern={onboardingData.primaryConcern}
                    initialData={{...onboardingData.questionnaire?.passportData, ...onboardingData.questionnaire?.patternData}}
                    segment="prakriti"
                    completedSegments={completedSegments}
                    skippedSegments={skippedSegments}
                    isRefreshMode={isRefreshMode}
                    vikritiSkipped={onboardingData.questionnaire?.vikritiSkipped}
                    onSegmentNavigate={handleSegmentNavigate}
                    onComplete={async (data) => {
                        await handlePrakritiStepComplete(data);
                    }} 
                    onPrakritiComplete={() => {}} 
                    skipAnswered={isCompleteMode}
                />
            )}

            {step === 9 && (
                <div className="min-h-screen bg-white">
                    {onboardingData.pattern ? (
                        <PatternRevelation 
                            detectedPattern={onboardingData.pattern}
                            userIntent={onboardingData.userIntent}
                            vikritiSkipped={onboardingData.questionnaire?.vikritiSkipped}
                            bodyConnections={formatBodyConnections()}
                            lifestyleFactors={formatLifestyleFactors()}
                            onComplete={handleOnboardingComplete}
                            isSubmitting={loading}
                        />
                    ) : (
                        <div className="h-screen flex flex-col items-center justify-center p-8 bg-sage-50/30">
                            <div className="w-20 h-20 mb-8 border-4 border-sage-100 border-t-sage-600 rounded-full animate-spin"></div>
                            <h2 className="text-3xl font-serif font-bold text-sage-900 mb-2">Analyzing Your Pattern...</h2>
                            <p className="text-sage-600">Connecting your health data with Ayurvedic wisdom.</p>
                            <div className="mt-12 flex gap-4">
                               <div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce"></div>
                               <div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                               <div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {step === 10 && (
                <ReviewAndSubmitScreen 
                    onboardingData={onboardingData}
                    initialAnswers={initialAnswers}
                    onEdit={(targetStep) => {
                        if (targetStep === 3) setIsEditMode(true);
                        setStep(targetStep);
                    }}
                    onSubmit={handleReviewSubmit}
                    isSubmitting={loading}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════
// PATTERN DETECTION
// ═══════════════════════════════════════════════════════════════════════

async function analyzePattern(data: any): Promise<DetectedPattern> {
    logger.log('[Onboarding] analyzePattern start:', { data });
    try {
        const { data: responseData, error } = await supabase.functions.invoke('analyze-onboarding-pattern', { body: data });
        if (error) {
            logger.error('[Onboarding] analyzePattern Edge Function Error:', error);
            throw error;
        }
        if (!responseData) {
            logger.error('[Onboarding] analyzePattern No response data');
            throw new Error('No data received from analysis');
        }
        logger.log('[Onboarding] analyzePattern Response:', responseData);
        return responseData;
    } catch (e) { 
        logger.warn('[Onboarding] Analysis failed, using fallback', e); 
    }

    // Minimal Client-side Fallback
    return {
        vikritiDosha: 'vata',
        prakritiDosha: 'vata',
        confidence: 0.7,
        insights: ["Consistent with your creative and energetic nature."],
    };
}
